import React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { useMe } from '@app/api';
import { CenteredLoader, NotFound } from '@app/core';
import { StudiesBar } from './components/StudiesBar';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useParticipationsByColumns } from '../components/useParticipationsByStatus';
import { useTranslation } from 'react-i18next';
import { StudiesConfirmed } from './components/StudiesConfirmed';
import { StudiesMatched } from './components/StudiesMatched';
import { StudiesAccepted } from './components/StudiesAccepted';
import { StudiesApplied } from './components/StudiesApplied';
import { StudiesPast } from './components/StudiesPast';

const useStyles = createUseStyles((theme: Theme) => ({
  page: {
    backgroundColor: theme.colors.grayBackground,
    width: '100%',
    minHeight: '100%',
  },
  globalContainer: {
    backgroundColor: theme.colors.white,
    width: '100%',
  },
  headerContainer: {
    maxWidth: theme.maxContentWidth.hugeBlock,
    margin: '0 auto',
  },
  titleContainer: {
    padding: [theme.marginBase * 3, theme.marginBase * 2],
    marginLeft: 0,
    backgroundColor: theme.colors.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.media.largeScreen]: {
      padding: theme.marginBase * 3,
      marginLeft: 32,
    },
  },
  title: { ...theme.fonts.h4 },
  titleIconContainer: {
    padding: theme.marginBase,
    borderRadius: theme.borderRadius.std,
    backgroundColor: theme.colors.grayBackground,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleIcon: {
    color: theme.colors.inkPrimary,
    fontSize: theme.iconSize.large,
  },
  tabsContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  tabs: {
    flex: 1,
    marginLeft: 2 * theme.marginBase,
    [theme.media.tablet]: {
      marginLeft: 0,
    },
  },
  mainContainer: {
    minHeight: '100%',
    width: '100%',
    margin: '0 auto',
  },
}));

export const Studies = () => {
  const classes = useStyles();
  const location = useLocation();
  const { t } = useTranslation();

  const { data: me } = useMe();
  const participationByStatus = useParticipationsByColumns();

  if (location.pathname === '/studies') {
    return <Navigate to="/studies/confirmed" replace />;
  }

  if (!participationByStatus) {
    return <CenteredLoader />;
  }
  if (!me) {
    return <NotFound />;
  }

  return (
    <div className={classes.page}>
      <div className={classes.globalContainer}>
        <div className={classes.titleContainer}>
          <h1 className={classes.title}>{t('studies.title')}</h1>
        </div>
        <StudiesBar participations={participationByStatus} />
      </div>
      <div className={classes.mainContainer}>
        <Routes>
          <Route
            path="past"
            element={
              <StudiesPast participations={participationByStatus.past} />
            }
          />
          <Route
            path="confirmed"
            element={
              <StudiesConfirmed
                participations={participationByStatus.confirmed}
              />
            }
          />
          <Route
            path="matched"
            element={
              <StudiesMatched participations={participationByStatus.invited} />
            }
          />
          <Route
            path="applied"
            element={
              <StudiesApplied participations={participationByStatus.applied} />
            }
          />
          <Route
            path="accepted"
            element={
              <StudiesAccepted
                participations={participationByStatus.accepted}
              />
            }
          />
        </Routes>
      </div>
    </div>
  );
};
